import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { enumIdentifierQualifier } from '@app/data-layer/enums/common';
import { enumEntityModelCode } from '@app/data-layer/enums/entities';
import { enumEntityType } from '@app/data-layer/enums/isf';
import { AddressModel, CountryModel, ShipmentModel, ValidationMessageModel } from '@app/data-layer/models/common';
import { EntityModel } from '@app/data-layer/models/entities';
import { EntrySummaryModel } from '@app/data-layer/models/entry-summaries';
import { CodeRepository, EntitiesRepository } from '@app/data-layer/repositories';
import { CANADA_COUNTRY_CODE, MEXICO_COUNTRY_CODE, USA_COUNTRY_CODE } from '@constants/constants';
import { DESC_ENTITY } from '@constants/desc-entity';
import { CUSTOM_MASK } from '@helpers/custom-validator';
import { FormHelper } from '@helpers/form.helper';
import { UtilsHelper } from '@helpers/utils-helper';
import { ISectionFormField } from '@interfaces/section-form-field';
import { TranslateService } from '@ngx-translate/core';
import { CompaniesService } from '@services/companies.service';
import { EntrySummaryStateService, EsOcrQuickFixService } from '@shared/es-mutual/services';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { IdentifierPipe } from '@shared/pipes-shared/pipes';
import { omit } from 'lodash-es';
import { Observable, of, ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, pairwise, switchMap, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-es-ocr-quick-fix-seller-consignee-details',
  templateUrl: './es-ocr-quick-fix-seller-consignee-details.component.html',
  styleUrls: ['./es-ocr-quick-fix-seller-consignee-details.component.scss'],
})
export class EsOcrQuickFixSellerConsigneeDetailsComponent implements OnInit, OnDestroy {
  @ViewChild(MissingFieldsComponent, { static: false }) public missingFieldsModal: MissingFieldsComponent;
  @Input() public sellerFields: ISectionFormField[];
  @Input() public consigneeFields: ISectionFormField[];
  @Input() public buyerFields: ISectionFormField[];

  @Input() public shipmentId: number;

  @Output() public dataSaved: EventEmitter<void> = new EventEmitter();

  public entrySummary: EntrySummaryModel;
  public shipment: ShipmentModel;
  public sellerFg: UntypedFormGroup;
  public consigneeFg: UntypedFormGroup;
  public buyerFg: UntypedFormGroup;
  public sellerDisplayErrors: Map<string, string> = new Map<string, string>();
  public consigneeDisplayErrors: Map<string, string> = new Map<string, string>();
  public buyerDisplayErrors: Map<string, string> = new Map<string, string>();
  public formErrors: { [name: string]: IErrorForm };

  public enumEntityModelCode: typeof enumEntityModelCode = enumEntityModelCode;
  public enumEntityType: typeof enumEntityType = enumEntityType;
  public sellerDisplayFields: ISectionFormField[] = [];
  public consigneeDisplayFields: ISectionFormField[] = [];
  public buyerDisplayFields: ISectionFormField[] = [];
  public consigneeIsSameAsImporter: string;
  public buyerIsSameAsImporter: string;
  public CUSTOM_MASK: any = CUSTOM_MASK;
  public entities: null | any[] = null;
  public companyName: AbstractControl;
  public companyLoading: boolean = false;
  private valueSellerCompany: null | string = null;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private fh: FormHelper,
    private ip: IdentifierPipe,
    private cdr: ChangeDetectorRef,
    private codeRepository: CodeRepository,
    private translateService: TranslateService,
    private companiesService: CompaniesService,
    private entitiesRepository: EntitiesRepository,
    private esOcrQuickFixService: EsOcrQuickFixService,
    private entrySummaryStateService: EntrySummaryStateService,
  ) {}

  ngOnInit(): void {
    const entry = this.esOcrQuickFixService.getEntrySummary();
    this.syncEntry(entry);

    this.initializeForms();

    this.setFieldsProperties();

    this.populateDisplayErrors();

    this.createFormErrors();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public isAnyFormBlockFieldEmpty(blockFields: ISectionFormField[], fg: UntypedFormGroup): boolean {
    let isFgInvalid: boolean = false;
    Object.keys(fg.controls).forEach((key) => {
      if (fg.controls[key].invalid) {
        isFgInvalid = true;
        return;
      }
    });

    let invalidFields = blockFields.find((field) => fg.get(field.name)?.invalid || !fg.get(field.name)?.value || isFgInvalid);
    return !!invalidFields;
  }

  public onSubmit(isNeedCheck: boolean = true): void {
    const sellerInvalidFields = this.fh.getInvalidFieldsByCode(this.sellerFg.value, this.enumEntityModelCode.SELLER);
    const consigneeInvalidFields = this.fh.getInvalidFieldsByCode(this.consigneeFg.value, this.enumEntityModelCode.CONSIGNEE);
    const buyerInvalidFields = this.fh.isEmptyEntityForm(this.buyerFg)
      ? {}
      : this.fh.getInvalidFieldsByCode(this.buyerFg.value, this.enumEntityModelCode.BUYER);
    if (
      isNeedCheck &&
      (Object.keys(sellerInvalidFields).length > 0 ||
        Object.keys(consigneeInvalidFields).length > 0 ||
        Object.keys(buyerInvalidFields).length > 0)
    ) {
      this.fh.markAsTouched(this.sellerFg);
      this.fh.markAsTouched(this.consigneeFg);
      this.fh.markAsTouched(this.buyerFg);
      const invalidFields: { [key: string]: ValidationMessageModel } = {
        ...sellerInvalidFields,
        ...consigneeInvalidFields,
        ...buyerInvalidFields,
      };

      this.openMissingFieldsModal(invalidFields);
      return;
    }

    const isBuyerFormEmpty = this.fh.isEmptyEntityForm(this.buyerFg);

    this.updateOrCreateEntity(this.sellerFg.value, this.sellerFg, 'sellerId', enumEntityModelCode.SELLER)
      .pipe(
        switchMap(() => this.updateOrCreateEntity(this.consigneeFg.value, this.consigneeFg, 'consigneeId', enumEntityModelCode.CONSIGNEE)),
        switchMap(() =>
          isBuyerFormEmpty && !this.shipment?.buyer
            ? of(this.entrySummary)
            : this.updateOrCreateEntity(this.buyerFg.value, this.buyerFg, 'buyerId', enumEntityModelCode.BUYER),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((entry) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.dataSaved.emit();
      });
  }

  public changeAddress(item: any) {
    const valuesToPatch = omit(item, ['identifier', 'identifierQualifier']) as EntityModel;
    this.sellerFg.patchValue(valuesToPatch);
    this.companyName.setValue(valuesToPatch.name);
    this.sellerFg.get('entityIdentifier').setValue(item.identifier);
    this.sellerFg.get('entityIdentifierQualifier').setValue(item.identifierQualifier);
  }

  public get filteredSellerFields(): ISectionFormField[] {
    const fieldsToIgnore = ['name'];
    return this.sellerFields.filter((field) => !fieldsToIgnore.includes(field.name));
  }

  public get isShowSellerState(): boolean {
    const country = this.sellerFg.get(['address', 'country']).value;
    return country && [CANADA_COUNTRY_CODE, USA_COUNTRY_CODE, MEXICO_COUNTRY_CODE].includes(country.code);
  }

  public get isSellerInvalid(): boolean {
    return this.isEntityInvalid(this.shipment?.seller);
  }

  public get isConsigneeInvalid(): boolean {
    return this.isEntityInvalid(this.shipment?.consignee);
  }

  public get isBuyerInvalid(): boolean {
    return this.isEntityInvalid(this.shipment?.buyer);
  }

  private isEntityInvalid(entity: EntityModel | null | undefined): boolean {
    if (!entity) {
      return false;
    }
    const hasEntityErrors: boolean = !!entity.validationMessages?.length;
    const hasAddressErrors: boolean = !UtilsHelper.isObjectEmpty(entity.address?.validationMessages ?? {});
    return hasEntityErrors || hasAddressErrors;
  }

  private openMissingFieldsModal(data) {
    this.missingFieldsModal.openCustomModal(data);
  }

  private updateOrCreateEntity(
    entity: EntityModel,
    formGroup: UntypedFormGroup,
    entityIdFieldName: string,
    entityCode: enumEntityModelCode,
  ) {
    return of(entity).pipe(
      takeUntil(this.destroy$),
      switchMap((entity) => {
        if (!entity.id || this.isPreviouslySavedAsImporter(formGroup, entityIdFieldName)) {
          entity.id = 0;
          entity.addressId = 0;
          entity.address.id = 0;
          entity.entityCode = entityCode;
          return this.entrySummaryStateService.createEntity(entity);
        } else {
          return this.entrySummaryStateService.updateEntity(entity.id, entity);
        }
      }),
      switchMap((entity) => {
        if (entity?.entityCode === enumEntityModelCode.SELLER && !this.shipment?.seller) {
          const data = {
            ...this.shipment,
            seller: {
              ...entity,
              entityCode: enumEntityModelCode.SELLER,
            },
            sellerId: entity.id,
          };

          return this.entrySummaryStateService.updateShipment(this.shipment, data);
        } else if (
          entity?.entityCode === enumEntityModelCode.CONSIGNEE &&
          (!this.shipment?.consignee || this.isPreviouslySavedAsImporter(formGroup, entityIdFieldName))
        ) {
          const data = {
            ...this.shipment,
            consignee: {
              ...entity,
              entityCode: enumEntityModelCode.CONSIGNEE,
            },
            consigneeId: entity.id,
          };

          return this.entrySummaryStateService.updateShipment(this.shipment, data);
        } else if (
          entity?.entityCode === enumEntityModelCode.BUYER &&
          (!this.shipment?.buyer || this.isPreviouslySavedAsImporter(formGroup, entityIdFieldName))
        ) {
          const data = {
            ...this.shipment,
            buyer: {
              ...entity,
              entityCode: enumEntityModelCode.BUYER,
            },
            buyerId: entity.id,
          };

          return this.entrySummaryStateService.updateShipment(this.shipment, data);
        } else {
          return of(this.shipment);
        }
      }),
      switchMap((shipment) => {
        const shipments = this.entrySummary.shipments.map((sh) => {
          if (sh.id === this.shipmentId) {
            return shipment;
          }

          return sh;
        });

        return this.entrySummaryStateService.updateEntrySummary({
          ...this.entrySummary,
          shipments,
        });
      }),
      switchMap((entry) => this.entrySummaryStateService.loadEntrySummary(entry.referenceNumber)),
      tap((entry) => {
        this.syncEntry(entry);
      }),
    );
  }

  private consigneeIsSameAsImporterText(entity: EntityModel, compareEntity: EntityModel): string {
    if (!entity || !compareEntity) {
      return;
    }

    if (entity?.id === compareEntity?.id) {
      const key = entity.entityCode && DESC_ENTITY[entity.entityCode] ? DESC_ENTITY[entity.entityCode] : DESC_ENTITY['IM'];
      return this.translateService.instant('LABELS.SAME_AS', {
        entityName: this.translateService.instant(key?.titleDesktop),
      });
    }
  }

  private setFieldsProperties() {
    const consignee = this.shipment?.consignee;
    const seller = this.shipment?.seller;
    const buyer = this.shipment?.buyer;
    const importer = this.entrySummary.importer;

    const sellerAddressDisplayLine = this.getAddressLines(seller?.address);
    const consigneeAddressDisplayLine = this.getAddressLines(consignee?.address);
    const buyerAddressDisplayLine = this.getAddressLines(buyer?.address);

    const consigneeIdentifier = consignee?.entityIdentifier;
    const consigneeIdentifierQualifier = consignee?.entityIdentifierQualifier;
    const consigneeIdentifierPrefix = consigneeIdentifierQualifier
      ? this.translateService.instant(`IDENTIFIER_QUALIFIER.${consigneeIdentifierQualifier}`)
      : '';
    const consigneeIdentifierSuffix = consigneeIdentifier ? this.translateService.instant(consigneeIdentifier) : '';

    const buyerIdentifier = buyer?.entityIdentifier;
    const buyerIdentifierQualifier = buyer?.entityIdentifierQualifier;
    const buyerIdentifierPrefix = buyerIdentifierQualifier
      ? this.translateService.instant(`IDENTIFIER_QUALIFIER.${buyerIdentifierQualifier}`)
      : '';
    const buyerIdentifierSuffix = buyerIdentifier ? this.translateService.instant(buyerIdentifier) : '';

    const sellerNameField = this.sellerFields.find((field) => field.name === 'name');
    const consigneeNameField = this.consigneeFields.find((field) => field.name === 'name');
    const buyerNameField = this.buyerFields.find((field) => field.name === 'name');

    this.sellerDisplayFields.push(
      {
        ...sellerNameField,
        type: 'text',
        label: 'LABELS.NAME',
        name: 'name',
        value: this.shipment?.seller?.name,
      },
      {
        type: 'text',
        label: 'LABELS.ADDRESS',
        name: 'address',
        value: sellerAddressDisplayLine,
      },
    );

    this.consigneeDisplayFields.push(
      {
        ...consigneeNameField,
        type: 'text',
        label: 'LABELS.NAME',
        name: 'name',
        value: this.shipment?.consignee?.name,
      },
      {
        type: 'text',
        label: 'LABELS.BUSINESS_ID_NUMBER',
        name: 'entityIdentifier',
        value: this.consigneeFg.get('entityIdentifier')?.value
          ? `${consigneeIdentifierPrefix} ${!!consigneeIdentifierSuffix ? this.ip.transform(consigneeIdentifierSuffix) : ''}`
          : '',
      },
      {
        type: 'text',
        label: 'LABELS.ADDRESS',
        name: 'address',
        value: consigneeAddressDisplayLine,
      },
    );

    this.buyerDisplayFields.push(
      {
        ...buyerNameField,
        type: 'text',
        label: 'LABELS.NAME',
        name: 'name',
        value: this.shipment?.buyer?.name,
      },
      {
        type: 'text',
        label: 'LABELS.BUSINESS_ID_NUMBER',
        name: 'entityIdentifier',
        value: this.buyerFg.get('entityIdentifier')?.value
          ? `${buyerIdentifierPrefix} ${!!buyerIdentifierSuffix ? this.ip.transform(buyerIdentifierSuffix) : ''}`
          : '',
      },
      {
        type: 'text',
        label: 'LABELS.ADDRESS',
        name: 'address',
        value: buyerAddressDisplayLine,
      },
    );

    this.consigneeIsSameAsImporter = this.consigneeIsSameAsImporterText(consignee, importer);
    this.buyerIsSameAsImporter = this.consigneeIsSameAsImporterText(buyer, importer);
  }

  private populateDisplayErrors() {
    Object.keys(this.sellerFg.controls).forEach((fieldName) => {
      switch (fieldName) {
        case 'name':
          this.sellerDisplayErrors.set(fieldName, this.fieldMissingErrorText(fieldName, this.sellerFg));
          break;
        default:
          break;
      }
    });

    Object.keys(this.consigneeFg.controls).forEach((fieldName) => {
      switch (fieldName) {
        case 'name':
        case 'entityIdentifier':
          this.consigneeDisplayErrors.set(fieldName, this.fieldMissingErrorText(fieldName, this.consigneeFg));
          break;
        default:
          break;
      }
    });

    Object.keys(this.buyerFg.controls).forEach((fieldName) => {
      switch (fieldName) {
        case 'name':
        case 'entityIdentifier':
          this.buyerDisplayErrors.set(fieldName, this.fieldMissingErrorText(fieldName, this.buyerFg));
          break;
        default:
          break;
      }
    });
  }

  private createFormErrors(): void {
    this.formErrors = this.fh.createErrorsForIsfFormGroup();
  }

  private fieldMissingErrorText(fieldName: string | string[], fg: UntypedFormGroup): string {
    return !fg.get(fieldName)?.value ? 'LABELS.MISSING' : '';
  }

  private initializeForms() {
    const seller = {
      ...this.shipment?.seller,
      entityCode: enumEntityModelCode.SELLER,
    };

    this.sellerFg = this.fh.createEntityModelFormGroup(enumEntityModelCode.SELLER);
    this.consigneeFg = this.fh.createEntityModelFormGroup(enumEntityModelCode.CONSIGNEE);
    this.buyerFg = this.fh.createEntityModelFormGroup(enumEntityModelCode.BUYER);

    this.sellerFg.patchValue(seller);
    this.consigneeFg.patchValue(this.shipment?.consignee);
    this.buyerFg.patchValue(this.shipment?.buyer);

    this.sellerFg.markAsDirty();
    this.consigneeFg.markAsDirty();
    this.buyerFg.markAsDirty();

    this.sellerFg.markAllAsTouched();
    this.consigneeFg.markAllAsTouched();
    this.buyerFg.markAllAsTouched();

    this.consigneeFg.get('entityIdentifierQualifier').valueChanges.subscribe((next) => {
      if (next !== enumIdentifierQualifier.ANI && !this.consigneeFg.get(['address', 'country']).value) {
        this.getConsigneeDefaultCountryAddress()
          .pipe(take(1))
          .subscribe((country) => {
            this.consigneeFg.get(['address', 'country']).setValue(country);
          });
      }
    });

    this.buyerFg
      .get('entityIdentifierQualifier')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((next) => {
        if (next !== enumIdentifierQualifier.ANI && !this.buyerFg.get(['address', 'country']).value) {
          this.getConsigneeDefaultCountryAddress()
            .pipe(take(1))
            .subscribe((country) => {
              this.buyerFg.get(['address', 'country']).setValue(country);
            });
        }
      });

    this.companyName = this.sellerFg.get('name');

    this.companyName.valueChanges
      .pipe(
        distinctUntilChanged(),
        pairwise(),
        filter(([pre, cur]) => {
          if (!pre || !cur) {
            return false;
          }
          return Math.abs(cur.length - (pre ? pre.length : 0)) === 1;
        }),
        debounceTime(600),
        distinctUntilChanged(),
        filter(([pre, cur]) => {
          return cur && cur.length >= 3;
        }),
        switchMap((value) => {
          if (this.valueSellerCompany && value[1].indexOf(value[0]) === 0 && this.valueSellerCompany === value[0]) {
            return of(seller);
          }
          this.companyLoading = true;
          const companyId: number = this.companiesService.getCompany() ? this.companiesService.getCompany().id : null;
          if (!companyId) {
            return of([]);
          }
          return this.entitiesRepository.searchByCompany(companyId, {
            name: value[1],
            type: seller?.entityCode,
          });
        }),
        map((value) => value),
        tap((values: any[]) => {
          if (values?.length === 0) {
            this.valueSellerCompany = this.companyName.value;
          } else {
            this.valueSellerCompany = null;
          }
        }),
      )
      .subscribe((values) => {
        this.entities = values;
        this.companyLoading = false;
        this.cdr.detectChanges();
      });

    this.fh.updateEntityValidation(true, this.sellerFg);
    this.fh.updateEntityValidation(true, this.consigneeFg);
    this.fh.updateEntityValidation(true, this.buyerFg);
  }

  private getConsigneeDefaultCountryAddress(): Observable<CountryModel> {
    return this.codeRepository.getCountry('US');
  }

  private syncEntry(entry: EntrySummaryModel) {
    this.entrySummary = entry;
    this.shipment = entry.shipments.find((sh) => sh.id === this.shipmentId);
  }

  private getAddressLines(data: AddressModel): string {
    if (!data) {
      return '';
    }

    const state = data.state ? data.state.description : null;
    const country = data.country ? data.country.description : null;
    const address = [data?.line1, data?.line2, data?.line3]
      .filter((part) => !!part)
      .join('\n')
      .trim();

    const line1 = [address, data.city]
      .filter((part) => !!part)
      .join(', ')
      .trim();
    const line2 = [country, state]
      .filter((part) => !!part)
      .join(', ')
      .trim();

    return line1 + line2;
  }

  private isPreviouslySavedAsImporter(formGroup: UntypedFormGroup, entityIdFieldName: string): boolean {
    const entityId = this.shipment[entityIdFieldName];
    const importer = this.entrySummary.importer;
    const isEntityFgSameAsImporter = UtilsHelper.formMatchesObject(formGroup, importer);
    return importer?.id === entityId && !isEntityFgSameAsImporter;
  }
}
